import { useEffect, useRef, useState } from "react";
import { Dialog, Select } from "@rio-cloud/rio-uikit";
import {
  OccurrenceStepEnum,
  OccurrenceStepLabelEnum,
  getOccurrenceStepLabelByStepValue,
} from "../../../../enums/occurrenceStep";
import { Occurrence, OccurrenceStep } from "../../../../models/occurrence";
import { fieldsLabels } from "./utils/fields-labels";

import ErrorDialog from "../../../../components/dialogs/ErrorDialog";
import ConfirmDialog from "../../../../components/dialogs/ConfirmDialog";
import StepReleaseForm from "./stepReleaseForm";
import { Scheduling } from "../../../../models/schedule";
import { fetchSchedulingByChassisList } from "../../../../api/fetchScheduling";
import { useIntl } from "react-intl";
import TasNumberRequiredDialog from "../../dialogs/tasNumberRequiredDialog";
import { mustSelectTasForStage } from "../../utils/tasStageGuard";


interface IChangeStepFormProps {
  reloadOccurrenceList: (occurrenceIdToRemove?: string, occurrenceIdToAdd?: string) => Promise<void>
  step: OccurrenceStep;
  occurrence: Occurrence | null;
  changeStep: (
    service: Occurrence,
    fromStepId: number,
    to: OccurrenceStepEnum
  ) => Promise<Occurrence | null | undefined>;
  save: (occurrence: Occurrence) => Promise<Occurrence | false>;
  onClose: () => void;
  requiredFields: string[];
  isValidStep: (requiredFields: string[], occurrence: Occurrence) => boolean;
  hasUnsavedChanges: () => boolean;
  getNotFilledRequiredFields: (
    requiredFields: string[],
    occurrence: Occurrence
  ) => string[];
  finishOccurrence: (occurrence: Occurrence, finalizationReasonType?: string, finalizationReasonDescription?: string, fromStepId?: number) => Promise<boolean>;
  occurrenceStepAvailable: string[];
}

const ChangeStepForm = (props: IChangeStepFormProps) => {
  const intl = useIntl();

  const currentStepDetails =
    props.occurrence?.steps?.find((s) => s.step === props.step.step) ?? props.step;
  const stepLabel =
    currentStepDetails.name ||
    (currentStepDetails.step
      ? getOccurrenceStepLabelByStepValue(currentStepDetails.step)
      : "") ||
    Object.values(OccurrenceStepLabelEnum).find((s) => s === props.step.name) ||
    "";
  const vehicleLabel =
    props.occurrence?.model ||
    props.occurrence?.vehicleName ||
    props.occurrence?.vehicleModel ||
    "";
  const step = currentStepDetails;
  const [nextStep, setNextStep] = useState<OccurrenceStepEnum | null>(null);
  const [loading, setLoading] = useState(false);
  const [finishLoading, setFinishLoading] = useState(false);
  const [concludeLoading, setConcludeLoading] = useState(false);
  const [scheduling, setScheduling] = useState<Scheduling | undefined>(undefined);
  const [errorDialog, setErrorDialog] = useState({
    show: false,
    title: "",
    body: <></>,
  });

  const [unSavedDialog, setUnSavedDialog] = useState({
    show: false,
    title: "",
    body: "",
  });

  const unsavedResolverRef = useRef<((confirmed: boolean) => void) | null>(null);

  const confirmUnsavedChanges = (): Promise<boolean> => {
    return new Promise((resolve) => {
      unsavedResolverRef.current = resolve;
      setUnSavedDialog({
        show: true,
        title: intl.formatMessage({ id: "intl-msg:occurrence.step.attention" }),
        body: intl.formatMessage({ id: "intl-msg:occurrence.step.unsavedChangesBody" }),
      });
    });
  };

  const resolveUnsavedDialog = (confirmed: boolean) => {
    setUnSavedDialog({ show: false, title: "", body: "" });
    unsavedResolverRef.current?.(confirmed);
    unsavedResolverRef.current = null;
  };

  const [releaseFormDialog, setReleaseFormDialog] = useState(false)
  const [cancelReleaseDialog, setCancelReleaseDialog] = useState(false)
  const skipReleaseCloseConfirmRef = useRef(false)
  const [pendingChange, setPendingChange] = useState<{
    fromStepId: number;
    toStep: OccurrenceStepEnum;
  } | null>(null);
  const [tasModalLoading, setTasModalLoading] = useState(false);

  useEffect(() => {
    const fetchScheduling = async () => {
      const occurrence = props.occurrence;

      if (occurrence?.scheduleNumber) {
        const scheduling = (await fetchSchedulingByChassisList([occurrence.vehicleChassis])).find(
          (e) => e.scheduleNumber === occurrence.scheduleNumber,
        );
        setScheduling(scheduling);
      }
    }

    fetchScheduling()
  }, [])

  const lastStep =
    props.occurrenceStepAvailable[props.occurrenceStepAvailable.length - 1];
  const isLastStep =
    OccurrenceStepEnum[lastStep as keyof typeof OccurrenceStepEnum] ===
    props.step.step;

  const isPreventive =
    props.occurrence?.occurrenceType === "P" ||
    props.occurrence?.occurrenceType === "PREVENTIVE";
  const isDealership =
    props.occurrence?.occurrenceType === "D" ||
    props.occurrence?.occurrenceType === "IN_DEALERSHIP";
  const shouldOpenStampModal =
    isPreventive ||
    (isDealership && !!props.occurrence?.volksEssentialsPlanContract);

  const closeModalWhenOptionIsRelease = () => {
    props.onClose();
    setFinishLoading(false);
    props.reloadOccurrenceList(props.occurrence?.id || '')
  }

  const showRequiredFieldsError = (
    title: string,
    message: string,
    requiredFields: string[],
    occurrence: Occurrence
  ) => {
    setErrorDialog({
      show: true,
      title,
      body: (
        <div>
          <div className="mb-2">{message}</div>
          <div className="mb-2">{intl.formatMessage({ id: "intl-msg:occurrence.step.requiredFields" })}</div>
          <ul className="list-disc list-inside">
            {props
              .getNotFilledRequiredFields(requiredFields, occurrence)
              .map((field) => (
                <li key={field}>
                  {fieldsLabels[field as keyof typeof fieldsLabels]}
                </li>
              ))}
          </ul>
        </div>
      ),
    });
  };

  const canOpenReleaseForm = async (): Promise<boolean> => {
    if (!props.occurrence) return false;

    if (props.hasUnsavedChanges()) {
      const confirmed = await confirmUnsavedChanges();
      if (!confirmed) return false;
    }

    const requiredFields = [...props.requiredFields];

    if (!props.isValidStep(requiredFields, props.occurrence)) {
      showRequiredFieldsError(
        intl.formatMessage({ id: "intl-msg:occurrence.step.errorOccurrenceTitle" }),
        intl.formatMessage({ id: "intl-msg:occurrence.step.errorOccurrenceBody" }),
        requiredFields,
        props.occurrence
      );
      return false;
    }

    return true;
  };

  const requestCloseReleaseForm = () => {
    if (skipReleaseCloseConfirmRef.current) {
      skipReleaseCloseConfirmRef.current = false;
      return;
    }
    setCancelReleaseDialog(true);
  };

  const closeReleaseForm = () => {
    skipReleaseCloseConfirmRef.current = true;
    setCancelReleaseDialog(false);
    setReleaseFormDialog(false);
    setFinishLoading(false);
  };

  const handleReleaseFormDialogChange: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
    const nextValue = typeof value === "function" ? value(releaseFormDialog) : value;
    if (!nextValue) {
      closeReleaseForm();
      return;
    }
    setReleaseFormDialog(true);
  };

  const closeOcurrence = async (): Promise<boolean> => {
    if (!props.occurrence) return false;

    if (props.hasUnsavedChanges()) {
      const confirmed = await confirmUnsavedChanges();
      if (!confirmed) return false;
    }

    const requiredFields = [...props.requiredFields];

    if (!props.isValidStep(requiredFields, props.occurrence)) {
      showRequiredFieldsError(
        intl.formatMessage({ id: "intl-msg:occurrence.step.errorOccurrenceTitle" }),
        intl.formatMessage({ id: "intl-msg:occurrence.step.errorOccurrenceBody" }),
        requiredFields,
        props.occurrence
      );
      return false;
    }

    setFinishLoading(true);

    let success = true;
    try {
      await props.finishOccurrence(props.occurrence, undefined, undefined, step?.id);
    } catch (error) {
      success = false;
    }

    if (!success) {
      setFinishLoading(false);
      setErrorDialog({
        show: true,
        title: intl.formatMessage({ id: "intl-msg:occurrence.step.errorOccurrenceTitle" }),
        body: (
          <div>
            {intl.formatMessage({ id: "intl-msg:occurrence.step.errorSupportBody" })}
          </div>
        ),
      });
      return false;
    }

    return true;
  }

  const advanceStep = async () => {
    if (!props.occurrence || !nextStep || !props.step.step || !props.step.id) return;

    const requiredFields = [...props.requiredFields];
    if (nextStep === OccurrenceStepEnum.PARTS) requiredFields.push('partOrders', 'partOrderParts');
    if (nextStep === OccurrenceStepEnum.PARTS_IN_TRANSIT) requiredFields.push('partOrders', 'partOrderParts');

    if (!props.isValidStep(requiredFields, props.occurrence ?? ({} as Occurrence))) {
      setErrorDialog({
        show: true,
        title: intl.formatMessage({ id: "intl-msg:occurrence.step.errorChangeStepTitle" }),
        body: (
          <div>
            <div className="mb-2">
              {intl.formatMessage({ id: "intl-msg:occurrence.step.errorChangeStepBody" })}
            </div>
            <div className="mb-2">
              {intl.formatMessage({ id: "intl-msg:occurrence.step.requiredFields" })}
            </div>
            <ul className="list-disc list-inside">
              {props
                .getNotFilledRequiredFields(
                  requiredFields,
                  props.occurrence
                )
                .map((field) => (
                  <li key={field}>
                    {fieldsLabels[field as keyof typeof fieldsLabels]}
                  </li>
                ))}
            </ul>
          </div>
        ),
      });
      return;
    }

    if (mustSelectTasForStage(nextStep, props.occurrence)) {
      setPendingChange({ fromStepId: props.step.id, toStep: nextStep });
      return;
    }

    setLoading(true);

    try {
      const newOccurrence = await props.changeStep(
        props.occurrence,
        props.step.id,
        nextStep
      );
      if (newOccurrence === undefined) {
        setLoading(false);
        return;
      }
      if (!newOccurrence) {
        setLoading(false);
        setErrorDialog({
          show: true,
          title: intl.formatMessage({ id: "intl-msg:occurrence.step.errorChangeStepTitle" }),
          body: (
            <div>
              {intl.formatMessage({ id: "intl-msg:occurrence.step.errorSupportBody" })}
            </div>
          ),
        });
        return;
      }

      props.onClose();
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      setErrorDialog({
        show: true,
        title: intl.formatMessage({ id: "intl-msg:occurrence.step.errorChangeStepTitle" }),
        body: <div>{error?.message ?? intl.formatMessage({ id: "intl-msg:occurrence.step.errorSupportBody" })}</div>,
      });
    }
  };

  const concludeStep = async () => {
    if (!props.occurrence || !props.step.step || !props.step.id) return;

    const nextStepIndex =
      Object.values(OccurrenceStepEnum).indexOf(props.step.step) + 1;
    const _nextStep =
      Object.values(OccurrenceStepEnum)[nextStepIndex];
    const requiredFields = [...props.requiredFields];
    if (_nextStep === OccurrenceStepEnum.PARTS) requiredFields.push('partOrders', 'partOrderParts');
    if (_nextStep === OccurrenceStepEnum.PARTS_IN_TRANSIT) requiredFields.push('partOrders', 'partOrderParts');

    if (!props.isValidStep(requiredFields, props.occurrence ?? ({} as Occurrence))) {
      setErrorDialog({
        show: true,
        title: intl.formatMessage({ id: "intl-msg:occurrence.step.errorCompleteStepTitle" }),
        body: (
          <div>
            <div className="mb-2">
              {intl.formatMessage({ id: "intl-msg:occurrence.step.errorCompleteStepBody" })}
            </div>
            <div className="mb-2">
              {intl.formatMessage({ id: "intl-msg:occurrence.step.requiredFields" })}
            </div>
            <ul className="list-disc list-inside">
              {props
                .getNotFilledRequiredFields(
                  requiredFields,
                  props.occurrence
                )
                .map((field) => (
                  <li key={field}>
                    {fieldsLabels[field as keyof typeof fieldsLabels]}
                  </li>
                ))}
            </ul>
          </div>
        ),
      });
      return;
    }

    if (mustSelectTasForStage(_nextStep, props.occurrence)) {
      setPendingChange({ fromStepId: props.step.id, toStep: _nextStep });
      return;
    }

    setConcludeLoading(true);
    try {
      const newOccurrence = await props.changeStep(
        props.occurrence,
        props.step.id,
        _nextStep
      );
      if (newOccurrence === undefined) {
        setConcludeLoading(false);
        return;
      }
      if (!newOccurrence) {
        setConcludeLoading(false);
        setErrorDialog({
          show: true,
          title: intl.formatMessage({ id: "intl-msg:occurrence.step.errorCompleteStepTitle" }),
          body: (
            <div>
              {intl.formatMessage({ id: "intl-msg:occurrence.step.errorSupportBody" })}
            </div>
          ),
        });
        return;
      }

      props.onClose();
      setConcludeLoading(false);
    } catch (error: any) {
      setConcludeLoading(false);
      setErrorDialog({
        show: true,
        title: intl.formatMessage({ id: "intl-msg:occurrence.step.errorCompleteStepTitle" }),
        body: <div>{error?.message ?? intl.formatMessage({ id: "intl-msg:occurrence.step.errorSupportBody" })}</div>,
      });
    }
  };

  return (
    <div className="w-full">
      <div className="text-size-h4 font-bold">{intl.formatMessage({ id: "intl-msg:occurrence.step.advanceStepTitle" })}</div>
      <div className="text-color-status-resting text-size-14 mb-8">
        {intl.formatMessage({ id: "intl-msg:occurrence.step.advanceStepDescription" })}
      </div>

      <div className="w-full border border-sky-600 rounded p-4">
        <div className="text-bold mb-4">
          {stepLabel}{vehicleLabel ? `: ${vehicleLabel}` : ""}
        </div>
        <hr />
        <div>
          <div className="flex justify-between py-4">
            <div>{intl.formatMessage({ id: "intl-msg:occurrence.step.start" })}</div>
            <div>
              {step?.dtStart?.toLocaleString("pt-BR", {
                dateStyle: "short",
                timeStyle: "short",
              }) || "—"}
            </div>
          </div>
          <div className="flex justify-between py-4">
            <div>{intl.formatMessage({ id: "intl-msg:occurrence.step.completion" })}</div>
            <div>
              {step?.dtEnd?.toLocaleString("pt-BR", {
                dateStyle: "short",
                timeStyle: "short",
              }) ?? intl.formatMessage({ id: "intl-msg:occurrence.step.notCompleted" })}
            </div>
          </div>
          <div className="flex justify-between py-4">
            <div>{intl.formatMessage({ id: "intl-msg:occurrence.step.duration" })}</div>
            <div>-</div>
          </div>
        </div>
        <div className="p-1 mt-4 border rounded-full text-sky-600 w-full border-sky-300 text-center bg-sky-50">
          {intl.formatMessage({ id: "intl-msg:occurrence.step.inProgress" })}
        </div>
      </div>

      <div className="flex justify-between w-full gap-6 mt-8">
        <div className="form-group form-group-lg w-full">
          <label htmlFor="nextStep">{intl.formatMessage({ id: "intl-msg:occurrence.step.selectStep" })}</label>
          <Select
            id="nextStep"
            options={props.occurrenceStepAvailable
              .filter((s) => {
                const stepValue =
                  OccurrenceStepEnum[s as keyof typeof OccurrenceStepEnum];

                // Ocultar a etapa atual
                if (stepValue === props.step.step) return false;

                // Ocultar etapas Deslocamento e Remoção se for ocorrência Preventiva
                if (props.occurrence?.occurrenceType === "P") {
                  return (
                    stepValue !== OccurrenceStepEnum.DISPLACEMENT &&
                    stepValue !== OccurrenceStepEnum.REMOVAL
                  );
                }

                return true;
              })
              .map((s) => ({
                label:
                  OccurrenceStepLabelEnum[
                    s as keyof typeof OccurrenceStepLabelEnum
                  ],
                value: OccurrenceStepEnum[s as keyof typeof OccurrenceStepEnum],
                id: s,
              }))}
            onChange={(e) => setNextStep(e?.value ?? null)}
            placeholder={intl.formatMessage({ id: "intl-msg:occurrence.step.selectStepPlaceholder" })}
            className="w-full"
          />
        </div>
      </div>

      <div className="flex justify-end mt-2 gap-2">
        <button
          className={`btn btn-primary w-[150px] ${
            loading ? "btn-loading disabled" : ""
          }`}
          type="button"
          disabled={!nextStep || loading}
          onClick={async () => {
            if (!props.occurrence || !nextStep || !props.step.step || !props.step.id) return;

            if (props.hasUnsavedChanges()) {
              const confirmed = await confirmUnsavedChanges();
              if (!confirmed) return;
            }

            await advanceStep();
          }}
        >
          {intl.formatMessage({ id: "intl-msg:occurrence.step.advanceStepTitle" })}
        </button>

        {props.step.step === OccurrenceStepEnum.RELEASE && (
          <button
            className={`btn btn-info w-[150px] ${
              finishLoading ? "btn-loading disabled" : ""
            }`}
            type="button"
            disabled={finishLoading}
            onClick={async () => {
              if (!(await canOpenReleaseForm())) return;

              if (shouldOpenStampModal) {
                setReleaseFormDialog(true);
                return;
              }

              const closed = await closeOcurrence();
              if (closed) {
                closeModalWhenOptionIsRelease();
              }
            }}
          >
            {intl.formatMessage({ id: "intl-msg:occurrence.step.completeOccurrence" })}
          </button>
        )}

        {isLastStep && props.step.step !== OccurrenceStepEnum.RELEASE && (
          <button
            className={`btn btn-info w-[150px] ${
              concludeLoading ? "btn-loading disabled" : ""
            }`}
            type="button"
            disabled={concludeLoading}
            onClick={async () => {
              if (!props.occurrence || !props.step.step || !props.step.id) return;

              if (props.hasUnsavedChanges()) {
                const confirmed = await confirmUnsavedChanges();
                if (!confirmed) return;
              }

              await concludeStep();
            }}
          >
            {intl.formatMessage({ id: "intl-msg:occurrence.step.completeStep" })}
          </button>
        )}
      </div>

      <ErrorDialog
        show={errorDialog.show}
        title={errorDialog.title}
        body={errorDialog.body}
        onClose={() => setErrorDialog({ show: false, title: "", body: <></> })}
      />

      <Dialog
          show={releaseFormDialog}
          title={intl.formatMessage({ id: "intl-msg:occurrence.step.release" })}
          body={
            releaseFormDialog ? (
              <StepReleaseForm 
                scheduling={scheduling} 
                occurrence={props.occurrence} 
                closeFirstModal={closeModalWhenOptionIsRelease}
                setReleaseFormDialog={handleReleaseFormDialogChange}
                closeOcurrence={closeOcurrence}
              />
            ) : null
          }
          showCloseButton
          className='exampleDialog'
          bsSize='md'
          onClose={requestCloseReleaseForm}
      />

      <ConfirmDialog
        show={cancelReleaseDialog}
        title={intl.formatMessage({ id: "intl-msg:occurrence.cancelReleaseTitle" })}
        body={intl.formatMessage({ id: "intl-msg:occurrence.cancelReleaseBody" })}
        onClose={() => setCancelReleaseDialog(false)}
        onConfirm={closeReleaseForm}
      />

      <ConfirmDialog
        show={unSavedDialog.show}
        title={unSavedDialog.title}
        body={unSavedDialog.body}
        onClose={() => resolveUnsavedDialog(false)}
        onConfirm={() => resolveUnsavedDialog(true)}
      />

      <TasNumberRequiredDialog
        show={!!pendingChange}
        occurrence={props.occurrence}
        loading={tasModalLoading}
        onCancel={() => setPendingChange(null)}
        onConfirm={async (tasNumber) => {
          if (!props.occurrence || !pendingChange) return;

          setTasModalLoading(true);
          try {
            const saved = await props.save({ ...props.occurrence, tasNumber });
            if (!saved) {
              setTasModalLoading(false);
              setErrorDialog({
                show: true,
                title: "Erro ao salvar número TAS",
                body: (
                  <div>
                    Verifique os dados e tente novamente ou entre em contato com
                    o suporte.
                  </div>
                ),
              });
              return;
            }

            const newOccurrence = await props.changeStep(
              { ...props.occurrence, tasNumber },
              pendingChange.fromStepId,
              pendingChange.toStep
            );
            if (!newOccurrence) {
              setTasModalLoading(false);
              setErrorDialog({
                show: true,
                title: "Erro ao alterar etapa",
                body: (
                  <div>
                    Verifique os dados e tente novamente ou entre em contato com
                    o suporte.
                  </div>
                ),
              });
              return;
            }

            setTasModalLoading(false);
            setPendingChange(null);
            props.onClose();
          } catch (error: any) {
            setTasModalLoading(false);
            setErrorDialog({
              show: true,
              title: "Erro ao alterar etapa",
              body: <div>{error?.message ?? "Verifique os dados e tente novamente ou entre em contato com o suporte."}</div>,
            });
          }
        }}
      />

    </div>
  );
};

export default ChangeStepForm;